import {
  useQuery,
  UseQueryResult,
  useQueryClient,
  useMutation,
  QueryKey,
} from "@tanstack/react-query";

import { SonicOfferData } from "../common/classes/SonicOffer";
import { getOffersAdmin } from "../services/api/offers/getOffersAdmin";
import { postUpdateOfferAdmin } from "../services/api/offers/postUpdateOfferAdmin";
import { getOffers } from "../services/api/offers/getOffers";
import { SonicOfferMetaData } from "../common/classes/SonicOfferMeta";

interface OfferData {
  total_entries: number;
  page: number;
  total_pages: number;
  offers: Array<SonicOfferData>;
  meta: Array<SonicOfferMetaData>;
}

const initialData: OfferData = {
  total_entries: 0,
  page: 0,
  total_pages: 0,
  offers: [],
  meta: [],
};

export const useGetOfferAdmin = (args: {
  auth_token?: string | null;
  isSelect: boolean;
  id?: string;
  page?: number;
  per?: number;
  search?: string;
  sort_by?: string;
  desc?: boolean;
}) => {
  const { auth_token, isSelect, id, page, per, search, sort_by, desc } = args;

  if (!auth_token) return { items: { ...initialData }, isLoading: false };

  const offerData: UseQueryResult<OfferData | null> = useQuery(
    ["offerAdmin", args],
    ({ signal }) =>
      getOffersAdmin(
        {
          auth_token: auth_token,
          isSelect: isSelect,
          id: id,
          page: page,
          per_page: per,
          search: search,
          sort_by: sort_by,
          desc: desc,
        },
        signal
      )
  );

  return {
    items: offerData.data ?? initialData,
    isLoading: offerData.isLoading,
    isError: offerData.isError,
  };
};

export const useUpdateOfferAdmin = (queryKey: QueryKey) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (args: {
      auth_token?: string | null;
      isSelect: boolean;
      offer_id: string;
      status?: string;
      memo?: string;
    }) => {
      if (!args.auth_token) return;

      await postUpdateOfferAdmin(
        args.auth_token,
        args.isSelect,
        args.offer_id,
        args.status,
        args.memo
      );
    },
    { onSuccess: () => queryClient.invalidateQueries(queryKey) }
  );

  return {
    update: mutation.mutateAsync,
    updateLoading: mutation.isLoading,
  };
};

// MyPage用
const initialOwnOfferData: {
  total_entries: number;
  page: number;
  total_pages: number;
  offers: Array<SonicOfferData>;
  meta: Array<SonicOfferMetaData>;
  isLoading: boolean;
  isError: boolean;
} = {
  total_entries: 0,
  page: 0,
  total_pages: 0,
  offers: [],
  meta: [],
  isLoading: false,
  isError: false,
};

export const useGetOffer = (args: {
  auth_token: string;
  page?: number;
  per?: number;
}) => {
  const { auth_token, page, per } = args;

  const offerData = useQuery(["offer", args], ({ signal }) =>
    getOffers(
      {
        auth_token: auth_token,
        page: page,
        per_page: per,
      },
      signal
    )
  );

  if (!offerData.data)
    return { ...initialOwnOfferData, isLoading: offerData.isLoading };

  return {
    offers: offerData.data.offers,
    meta: offerData.data.meta,
    total_entries: offerData.data.total_entries,
    page: offerData.data.page,
    total_pages: offerData.data.total_pages,
    isLoading: offerData.isLoading,
    isError: offerData.isError,
  };
};
